import { computed, toRef, Ref } from "vue";
import { useDataFiltersStore } from "@/store/data_filters";
import { refDebounced } from "@vueuse/core";
import { payloadToQueryParams } from "@/services/data_filters";
import * as lodash from "lodash-es";

// TypeScript utility type to convert union types to intersection types
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
  k: infer I,
) => void
  ? I
  : never;

/**
 * Provides debounced versions of filter values to prevent excessive API calls
 * when users rapidly change filters.
 *
 * These debounced values should be used in TanStack Query query keys instead of
 * the raw store values to prevent queries from refetching on every keystroke or
 * rapid filter change.
 */
export function useDebouncedFilters() {
  const dataFiltersStore = useDataFiltersStore();

  // Debounce times for different filter types
  const SELECTION_DEBOUNCE = 1250;
  const SEARCH_DEBOUNCE = 250; // Search inputs

  // Selection filters (shorter debounce)
  // For array filters, bypass debounce when empty (for instant reset behavior)
  const includedResourcesRef = toRef(dataFiltersStore, "includedResources");
  const debouncedIncludedResources = refDebounced(
    includedResourcesRef,
    SELECTION_DEBOUNCE,
  );

  const includedCompetitorsRef = toRef(dataFiltersStore, "includedCompetitors");
  const debouncedIncludedCompetitors = refDebounced(
    includedCompetitorsRef,
    SELECTION_DEBOUNCE,
  );

  const includedDomainsRef = toRef(dataFiltersStore, "includedDomains");
  const debouncedIncludedDomains = refDebounced(
    includedDomainsRef,
    SELECTION_DEBOUNCE,
  );

  const includedChannelsRef = toRef(dataFiltersStore, "includedChannels");
  const debouncedIncludedChannels = refDebounced(
    includedChannelsRef,
    SELECTION_DEBOUNCE,
  );

  const includedConversionGroupsRef = toRef(
    dataFiltersStore,
    "includedConversionGroups",
  );
  const debouncedIncludedConversionGroups = refDebounced(
    includedConversionGroupsRef,
    SELECTION_DEBOUNCE,
  );

  const includedAdNetworksRef = toRef(dataFiltersStore, "includedAdNetworks");
  const debouncedIncludedAdNetworks = refDebounced(
    includedAdNetworksRef,
    SELECTION_DEBOUNCE,
  );

  const includedTagsRef = toRef(dataFiltersStore, "includedTags");
  const debouncedIncludedTags = refDebounced(
    includedTagsRef,
    SELECTION_DEBOUNCE,
  );

  const excludedTagsRef = toRef(dataFiltersStore, "excludedTags");
  const debouncedExcludedTags = refDebounced(
    excludedTagsRef,
    SELECTION_DEBOUNCE,
  );

  const debouncedTemporalFilter = refDebounced(
    toRef(dataFiltersStore, "temporalFilter"),
    SELECTION_DEBOUNCE,
  );

  const debouncedSeparateResources = refDebounced(
    toRef(dataFiltersStore, "separateResources"),
    SELECTION_DEBOUNCE,
  );

  const debouncedMetricTotals = refDebounced(
    toRef(dataFiltersStore, "metricTotals"),
    SELECTION_DEBOUNCE,
  );

  // Metric filter criteria (medium debounce)
  const debouncedMpFilterCriteria = refDebounced(
    toRef(dataFiltersStore, "mpFilterCriteria"),
    SELECTION_DEBOUNCE,
  );

  const debouncedCmoPostsFilterCriteria = refDebounced(
    toRef(dataFiltersStore, "cmoPostsFilterCriteria"),
    SELECTION_DEBOUNCE,
  );

  // Search inputs (longer debounce)
  const debouncedSocialMediaSearchInput = refDebounced(
    toRef(dataFiltersStore, "socialMediaSearchInput"),
    SEARCH_DEBOUNCE,
  );

  const debouncedFeedItemSearchInput = refDebounced(
    toRef(dataFiltersStore, "feedItemSearchInput"),
    SEARCH_DEBOUNCE,
  );

  const debouncedCompetitorMonitorSearchInput = refDebounced(
    toRef(dataFiltersStore, "competitorMonitorSearchInput"),
    SEARCH_DEBOUNCE,
  );

  // Ecommerce filters
  const debouncedCheckedEcommerceCategories = refDebounced(
    toRef(dataFiltersStore, "checkedEcommerceCategories"),
    SELECTION_DEBOUNCE,
  );

  const debouncedCheckedEcommerceBrands = refDebounced(
    toRef(dataFiltersStore, "checkedEcommerceBrands"),
    SELECTION_DEBOUNCE,
  );

  // Share of Search filters
  const debouncedCheckedShareOfSearchCountries = refDebounced(
    toRef(dataFiltersStore, "checkedShareOfSearchCountries"),
    SELECTION_DEBOUNCE,
  );

  const debouncedCheckedShareOfSearchCategories = refDebounced(
    toRef(dataFiltersStore, "checkedShareOfSearchCategories"),
    SELECTION_DEBOUNCE,
  );

  const debouncedCheckedShareOfSearchKeywords = refDebounced(
    toRef(dataFiltersStore, "checkedShareOfSearchKeywords"),
    SELECTION_DEBOUNCE,
  );

  // Status filters
  const debouncedRecommendationStatus = refDebounced(
    toRef(dataFiltersStore, "recommendationStatus"),
    SELECTION_DEBOUNCE,
  );

  const debouncedCountryDataType = refDebounced(
    toRef(dataFiltersStore, "countryDataType"),
    SELECTION_DEBOUNCE,
  );

  const debouncedSearchTermDataType = refDebounced(
    toRef(dataFiltersStore, "searchTermDataType"),
    SELECTION_DEBOUNCE,
  );

  // Sorting
  const debouncedWaPagesSortMetric = refDebounced(
    toRef(dataFiltersStore, "waPagesSortMetric"),
    SELECTION_DEBOUNCE,
  );

  const debouncedSocialMediaSortByMetric = refDebounced(
    toRef(dataFiltersStore, "socialMediaSortByMetric"),
    SELECTION_DEBOUNCE,
  );

  const debouncedCompetitorMonitorSortByMetric = refDebounced(
    toRef(dataFiltersStore, "competitorMonitorSortByMetric"),
    SELECTION_DEBOUNCE,
  );

  // Metric sets
  const debouncedMarketingMetricSet = refDebounced(
    toRef(dataFiltersStore, "marketingMetricSet"),
    SELECTION_DEBOUNCE,
  );

  const debouncedWebsiteAnalyticsMetricSet = refDebounced(
    toRef(dataFiltersStore, "websiteAnalyticsMetricSet"),
    SELECTION_DEBOUNCE,
  );

  return {
    // Core filters
    debouncedIncludedResources,
    debouncedIncludedCompetitors,
    debouncedIncludedDomains,
    debouncedIncludedChannels,
    debouncedIncludedConversionGroups,
    debouncedIncludedAdNetworks,
    debouncedIncludedTags,
    debouncedExcludedTags,
    debouncedTemporalFilter,
    debouncedSeparateResources,
    debouncedMetricTotals,

    // Metric filters
    debouncedMpFilterCriteria,
    debouncedCmoPostsFilterCriteria,

    // Search inputs
    debouncedSocialMediaSearchInput,
    debouncedFeedItemSearchInput,
    debouncedCompetitorMonitorSearchInput,

    // Ecommerce
    debouncedCheckedEcommerceCategories,
    debouncedCheckedEcommerceBrands,

    // Share of Search
    debouncedCheckedShareOfSearchCountries,
    debouncedCheckedShareOfSearchCategories,
    debouncedCheckedShareOfSearchKeywords,

    // Status
    debouncedRecommendationStatus,
    debouncedCountryDataType,
    debouncedSearchTermDataType,

    // Sorting
    debouncedWaPagesSortMetric,
    debouncedSocialMediaSortByMetric,
    debouncedCompetitorMonitorSortByMetric,

    // Metric sets
    debouncedMarketingMetricSet,
    debouncedWebsiteAnalyticsMetricSet,
  };
}

/**
 * Creates a debounced ref for a specific filter
 * Useful for one-off debouncing needs
 *
 * @param filterRef - A ref to debounce
 * @param debounceMs - Debounce delay in milliseconds (default: 400ms)
 */
export function useDebouncedFilter<T>(
  filterRef: Ref<T>,
  debounceMs = 400,
): Ref<T> {
  return refDebounced(filterRef, debounceMs);
}

/**
 * Debounced version of useFullDataFilters()
 * Returns all filter values with debouncing applied
 * This is the core function that other debounced composables build on
 */
function useDebouncedFullDataFilters() {
  const {
    debouncedIncludedResources,
    debouncedIncludedChannels,
    debouncedIncludedTags,
    debouncedExcludedTags,
    debouncedIncludedAdNetworks,
    debouncedIncludedConversionGroups,
    debouncedTemporalFilter,
    debouncedSeparateResources,
    debouncedIncludedDomains,
    debouncedIncludedCompetitors,
    debouncedCheckedEcommerceCategories,
    debouncedCheckedEcommerceBrands,
    debouncedCheckedShareOfSearchCountries,
    debouncedCheckedShareOfSearchCategories,
    debouncedCheckedShareOfSearchKeywords,
    debouncedFeedItemSearchInput,
  } = useDebouncedFilters();

  const dataFiltersStore = useDataFiltersStore();

  return {
    channel: {
      channel__in: debouncedIncludedChannels,
    },
    includedResources: {
      resource__in: debouncedIncludedResources,
    },
    selectionTemporalFilter: {
      selection_from: computed(
        () => debouncedTemporalFilter.value.selection.start,
      ),
      selection_to: computed(() => debouncedTemporalFilter.value.selection.end),
    },
    temporalFilter: {
      selection_from: computed(
        () => debouncedTemporalFilter.value.selection.start,
      ),
      selection_to: computed(() => debouncedTemporalFilter.value.selection.end),
      comparison_from: computed(
        () => debouncedTemporalFilter.value.comparison?.start ?? null,
      ),
      comparison_to: computed(
        () => debouncedTemporalFilter.value.comparison?.end ?? null,
      ),
    },
    includedTags: {
      tags__in: debouncedIncludedTags,
    },
    excludedTags: {
      not__tags__in: debouncedExcludedTags,
    },
    subcategory: {
      subcategory__in: debouncedIncludedAdNetworks,
    },
    conversionGroup: {
      conversion_group__in: debouncedIncludedConversionGroups,
    },
    conversion: {
      conversion__in: computed(() => dataFiltersStore.includedConversions),
    },
    brands: { brand: debouncedCheckedEcommerceBrands },
    categories: {
      category: debouncedCheckedEcommerceCategories,
    },
    domains: {
      domain__in: debouncedIncludedDomains,
    },
    separateResources: {
      separateResources: debouncedSeparateResources,
    },
    includeAllEvents: {
      is_conversion_event: computed(() =>
        dataFiltersStore.includeAllEvents ? null : true,
      ),
    },
    includedUsers: {
      user__in: computed(() => dataFiltersStore.includedUsers),
      created_by__in: computed(() => dataFiltersStore.includedUsers),
    },
    includedFeedItemTypes: {
      item_type__in: computed(() => dataFiltersStore.includedFeedItemTypes),
    },
    feedItemSearchInput: {
      search: debouncedFeedItemSearchInput,
    },
    shareOfSearchCompetitors: {
      competitorIds: computed(
        () =>
          debouncedIncludedCompetitors.value.map((pk) => pk ?? "null") as (
            | number
            | null
          )[],
      ),
    },
    shareOfSearchCategories: {
      categories: debouncedCheckedShareOfSearchCategories,
    },
    shareOfSearchCountries: {
      countries: debouncedCheckedShareOfSearchCountries,
    },
    shareOfSearchKeywords: {
      keywords: debouncedCheckedShareOfSearchKeywords,
    },
    yearFilter: {
      year: computed(() => dataFiltersStore.yearFilter),
    },
    fieldsFilter: {
      fields: toRef(dataFiltersStore, "fields"),
    },
  };
}

/**
 * Debounced version of useDataFilters()
 * Use this instead of the regular useDataFilters() to get automatic debouncing
 *
 * @param keys - Array of filter keys to include
 * @param useGlobalFilters - Whether to include global filters (default: true)
 *
 * @example
 * ```typescript
 * const { tags__in, resource__in } = useDebouncedDataFilters(['includedTags', 'includedResources']);
 * ```
 */
export function useDebouncedDataFilters<
  K extends keyof ReturnType<typeof useDebouncedFullDataFilters>,
>(
  keys: K[],
  useGlobalFilters = true,
): UnionToIntersection<ReturnType<typeof useDebouncedFullDataFilters>[K]> {
  const fullDataFilters = useDebouncedFullDataFilters();
  const dataFilters = Object.fromEntries(
    Object.values(lodash.pick(fullDataFilters, keys))
      .flatMap((obj) => Object.entries(obj as Record<string, Ref>))
      .map(([k, v]) => [k, computed(() => payloadToQueryParams(v.value))]),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) as any as UnionToIntersection<
    ReturnType<typeof useDebouncedFullDataFilters>[K]
  >;

  if (useGlobalFilters) {
    return dataFilters;
  } else {
    return Object.fromEntries(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Object.entries(dataFilters as any).map(([k, _v]) => [
        k,
        computed(() => null),
      ]),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ) as any as UnionToIntersection<
      ReturnType<typeof useDebouncedFullDataFilters>[K]
    >;
  }
}

/**
 * Debounced version of useCalculatedResources()
 * Use this for resource filtering with automatic debouncing
 */
export function useDebouncedCalculatedResources(
  selectedResourceIds?: Ref<number[]>,
  useGlobalFilters = true,
): {
  resource__in: Ref<number[] | null>;
} {
  return selectedResourceIds
    ? {
        resource__in: computed(
          () =>
            payloadToQueryParams(selectedResourceIds.value) as number[] | null,
        ),
      }
    : useDebouncedDataFilters(["includedResources"], useGlobalFilters);
}

/**
 * Debounced version of useConversionGroups()
 * Use this for conversion group filtering with automatic debouncing
 */
export function useDebouncedConversionGroups(
  conversionGroups?: Ref<number[]>,
  useGlobalFilters = true,
): {
  conversion_group__in: Ref<number[] | null>;
} {
  return conversionGroups
    ? {
        conversion_group__in: computed(
          () => payloadToQueryParams(conversionGroups.value) as number[] | null,
        ),
      }
    : useDebouncedDataFilters(["conversionGroup"], useGlobalFilters);
}

/**
 * Debounced version of useDetachedTagFilter()
 * Use this for tag filtering with automatic debouncing
 */
export function useDebouncedDetachedTagFilter(
  useGlobalFilters = true,
  includedTags?: Ref<number[]>,
  excludedTags?: Ref<number[]>,
  includedTagsLogic?: Ref<"AND" | "OR">,
): {
  tags__in: Ref<number[] | null>;
  not__tags__in: Ref<number[] | null>;
  tags_logic: Ref<"AND" | "OR">;
} {
  if (includedTags) {
    // Widget-specific tags (passed as parameters)
    return {
      tags__in: computed(
        () => payloadToQueryParams(includedTags.value) as number[] | null,
      ),
      not__tags__in: computed(
        () =>
          payloadToQueryParams(excludedTags?.value ?? []) as number[] | null,
      ),
      tags_logic: computed(() => includedTagsLogic?.value ?? "OR"),
    };
  } else {
    // Global filters from store
    const filters = useDebouncedDataFilters(
      ["includedTags", "excludedTags"],
      useGlobalFilters,
    );
    return {
      ...filters,
      tags_logic: computed(() => "OR"), // Global filters always use OR logic
    };
  }
}
